/**
 * Cache reset utility
 * Cleans Service Worker registrations, Cache Storage, and optionally
 * localStorage/sessionStorage (preserving auth, language and theme keys).
 */

const PROTECTED_PREFIXES = [
  'sb-',                 // supabase auth tokens
  'supabase.auth',       // supabase auth tokens (legacy)
  'i18nextLng',          // language
  'theme',               // theme
  'gps-prompt',          // global GPS prompt flags
];

function isProtectedKey(key: string): boolean {
  return PROTECTED_PREFIXES.some((p) => key.startsWith(p));
}

export interface ResetOptions {
  clearStorage?: boolean; // default false — preserves login & prefs
}

export async function resetClientCache(opts: ResetOptions = {}): Promise<{
  unregistered: number;
  cachesDeleted: number;
  storageCleared: boolean;
}> {
  let unregistered = 0;
  let cachesDeleted = 0;
  let storageCleared = false;

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
      unregistered = regs.length;
    }
  } catch (e) {
    console.warn('[cacheReset] SW unregister failed:', e);
  }

  try {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
      cachesDeleted = names.length;
    }
  } catch (e) {
    console.warn('[cacheReset] Cache Storage delete failed:', e);
  }

  if (opts.clearStorage) {
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && !isProtectedKey(k)) toRemove.push(k);
      }
      toRemove.forEach((k) => localStorage.removeItem(k));

      const toRemoveSession: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && !isProtectedKey(k)) toRemoveSession.push(k);
      }
      toRemoveSession.forEach((k) => sessionStorage.removeItem(k));
      storageCleared = true;
    } catch (e) {
      console.warn('[cacheReset] storage clear failed:', e);
    }
  }

  console.log('[cacheReset] done:', { unregistered, cachesDeleted, storageCleared });
  return { unregistered, cachesDeleted, storageCleared };
}

/**
 * Expose `__resetCache()` on window for manual debugging from devtools.
 * Usage: __resetCache()  or  __resetCache({ clearStorage: true })
 */
export function exposeCacheResetGlobal(): void {
  if (typeof window === 'undefined') return;
  (window as any).__resetCache = (opts?: ResetOptions) =>
    resetClientCache(opts).then((r) => {
      console.log('[__resetCache] reloading…');
      setTimeout(() => window.location.reload(), 200);
      return r;
    });
}
